import { useEffect, useState } from "react";
import { Volume2, RefreshCw, Cloud, Cpu, Flame, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/** Moderator dashboard: how often the "Listen to ›" pill is pressed, per page. */
export default function AdminListen() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [heatmap, setHeatmap] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/listen/stats", { params: { days: d } });
      setStats(data);
    } catch { toast.error("Could not load Listen analytics"); }
    finally { setLoading(false); }
  };

  const openHeatmap = async (path) => {
    try {
      const { data } = await api.get("/listen/heatmap", { params: { path } });
      setHeatmap(data);
    } catch { toast.error("Could not load heatmap for " + path); }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-listen-page">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3">
          <Volume2 className="w-8 h-8 text-emerald-600" /> Listen Analytics
        </h1>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => { const d = Number(e.target.value); setDays(d); load(d); }}
            className="border border-zinc-300 bg-white px-2 py-1.5 text-sm" data-testid="admin-listen-range">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button onClick={() => load(days)} disabled={loading} data-testid="admin-listen-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 text-sm font-700 text-zinc-700 hover:bg-zinc-50 disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!stats ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <Card label="Total Listen starts" value={stats.total_starts} />
            <Card label="Device voice" value={stats.total_device} icon={<Cpu className="w-4 h-4" />} tone="cyan" />
            <Card label="AI narration" value={stats.total_cloud} icon={<Cloud className="w-4 h-4" />} tone="purple" />
          </div>

          <h2 className="font-heading font-700 text-lg mb-3">Most listened-to pages</h2>
          {stats.top_pages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No Listen events yet.</p>
          ) : (
            <div className="border border-zinc-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2">Page</th>
                    <th className="text-right px-4 py-2">Starts</th>
                    <th className="text-right px-4 py-2">Resumes</th>
                    <th className="text-right px-4 py-2">Stops</th>
                    <th className="text-right px-4 py-2">Device</th>
                    <th className="text-right px-4 py-2">AI</th>
                    <th className="text-right px-4 py-2">Heatmap</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_pages.map((p) => (
                    <tr key={p.path} className="border-t border-zinc-100" data-testid={`admin-listen-row-${p.path.replaceAll("/", "-")}`}>
                      <td className="px-4 py-2 font-mono">
                        <a href={p.path} target="_blank" rel="noreferrer" className="text-primary hover:underline">{p.path}</a>
                      </td>
                      <td className="px-4 py-2 text-right font-mono">{p.starts.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono">{p.resumes.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono">{p.stops.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono">{p.device.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono">{p.cloud.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => openHeatmap(p.path)} data-testid={`admin-listen-heatmap-${p.path.replaceAll("/", "-")}`}
                          className="inline-flex items-center gap-1 text-xs font-700 text-rose-700 hover:underline">
                          <Flame className="w-3.5 h-3.5" /> Heatmap
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {heatmap && (
        <div className="fixed inset-0 z-[9999] bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="admin-listen-heatmap-modal" onClick={() => setHeatmap(null)}>
          <div className="bg-white border border-zinc-300 shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-zinc-200">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-rose-700 flex items-center gap-1.5">
                  <Flame className="w-4 h-4" /> Reader heatmap
                </div>
                <h3 className="font-heading font-900 text-lg mt-0.5 font-mono">{heatmap.path}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Where readers jumped in ({heatmap.total_blocks || Object.keys(heatmap.hits || {}).length} paragraphs tracked).
                  Bars show relative jumps — long bars = interesting or hard-to-follow paragraphs.
                </p>
              </div>
              <button onClick={() => setHeatmap(null)} data-testid="admin-listen-heatmap-close"
                className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
            </div>
            <HeatmapBody heatmap={heatmap} />
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, icon, tone }) {
  const cls = tone === "cyan" ? "border-[#0e7490] text-[#0e7490]"
    : tone === "purple" ? "border-violet-500 text-violet-700"
    : "border-zinc-300 text-zinc-800";
  return (
    <div className={`border bg-white p-4 ${cls}`}>
      <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide text-muted-foreground">
        {icon}{label}
      </div>
      <div className="font-heading font-900 text-3xl mt-1">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}
function HeatmapBody({ heatmap }) {
  const hits = heatmap.hits || {};
  const previews = heatmap.previews || {};
  const indices = Object.keys(hits).map((k) => Number(k)).sort((a, b) => a - b);
  const max = Math.max(1, ...Object.values(hits).map(Number));
  if (indices.length === 0) {
    return (
      <p className="p-6 text-sm text-muted-foreground text-center">
        No paragraph-jump events recorded yet. Readers must tap a paragraph mid-session for this data to appear.
      </p>
    );
  }
  return (
    <ol className="p-5 space-y-2" data-testid="admin-listen-heatmap-body">
      {indices.map((idx) => {
        const count = Number(hits[idx] || 0);
        const pct = Math.round((count / max) * 100);
        return (
          <li key={idx} className="flex items-center gap-3 text-sm">
            <span className="w-8 text-right font-mono text-xs text-zinc-500">§{idx + 1}</span>
            <div className="flex-1 h-6 bg-zinc-100 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-rose-400 to-rose-600"
                style={{ width: `${pct}%` }} />
              <span className="relative z-10 px-2 leading-6 text-xs font-700 text-zinc-800 truncate block">
                {previews[idx] || "(no preview captured)"}
              </span>
            </div>
            <span className="w-14 text-right font-mono text-xs text-zinc-700 tabular-nums">{count.toLocaleString()}</span>
          </li>
        );
      })}
    </ol>
  );
}
